import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { centsToDollarString } from "./zipSlotAdsHelpers";

const AutoRechargeForm = ({
  balance,
  onSave,
  saving = false,
  error = "",
  success = "",
  onDismissSuccess,
  hasPaymentMethod = false,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      autoRechargeEnabled: false,
      autoRechargeThresholdDollars: "25.00",
      autoRechargeAmountDollars: "100.00",
    },
    mode: "onChange",
  });

  const enabled = watch("autoRechargeEnabled");

  useEffect(() => {
    reset({
      autoRechargeEnabled: Boolean(balance?.autoRechargeEnabled),
      autoRechargeThresholdDollars:
        centsToDollarString(balance?.autoRechargeThresholdCents) || "25.00",
      autoRechargeAmountDollars:
        centsToDollarString(balance?.autoRechargeAmountCents) || "100.00",
    });
  }, [
    balance?.autoRechargeEnabled,
    balance?.autoRechargeThresholdCents,
    balance?.autoRechargeAmountCents,
    reset,
  ]);

  const submit = handleSubmit((data) => {
    onSave(data);
  });

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Auto-recharge
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When your balance falls below the threshold, the card on file is charged
        the recharge amount. This never writes your balance from the browser.
      </Typography>

      {!hasPaymentMethod ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Auto-recharge needs a card on file. Complete a top-up once so Stripe
          can save a payment method.
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" onClose={onDismissSuccess} sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}

      <Box component="form" onSubmit={submit}>
        <Stack spacing={2}>
          <Controller
            name="autoRechargeEnabled"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(field.value)}
                    onChange={(_, checked) => field.onChange(checked)}
                    disabled={saving}
                  />
                }
                label="Enable auto-recharge"
              />
            )}
          />

          <Controller
            name="autoRechargeThresholdDollars"
            control={control}
            rules={{
              validate: (value) => {
                if (!enabled) return true;
                const n = Number(value);
                if (!Number.isFinite(n) || n < 0) {
                  return "Enter a non-negative threshold";
                }
                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Recharge when balance falls below"
                type="number"
                disabled={saving || !enabled}
                error={!!errors.autoRechargeThresholdDollars}
                helperText={errors.autoRechargeThresholdDollars?.message}
                inputProps={{ min: 0, step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            name="autoRechargeAmountDollars"
            control={control}
            rules={{
              validate: (value) => {
                if (!enabled) return true;
                const n = Number(value);
                if (!Number.isFinite(n) || n <= 0) {
                  return "Enter a positive recharge amount";
                }
                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Recharge amount"
                type="number"
                disabled={saving || !enabled}
                error={!!errors.autoRechargeAmountDollars}
                helperText={errors.autoRechargeAmountDollars?.message}
                inputProps={{ min: 0.01, step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Box>
            <LoadingButton type="submit" variant="contained" loading={saving}>
              Save auto-recharge settings
            </LoadingButton>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
};

export default AutoRechargeForm;
